import {
    ChangeDetectionStrategy,
    ViewEncapsulation,
    Component,
    inject,
    ElementRef,
    ViewChild,
    input,
    computed,
    signal,
    effect,
    DestroyRef,
} from '@angular/core';
import { AbstractControl, NG_VALIDATORS, ValidationErrors, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ControlValueAccessorValidatorBase } from '@fi-app/shared/control-value-accessor-validator-base';
import {
    IAccessRightCoreFilter,
    IAccessRightFilterCollection,
    IAccessRightSelectionFiltersCollection,
} from '@fi-shared/models';
import {
    FiSharingSettingsContentComponent,
    ISharingSettingsContentConfig,
} from '@fi-shared/access-right-filters/fi-sharing-settings-content/fi-sharing-settings-content.component';
import { ModuleType } from '@fi-shared/types';
import { AppTextService } from '@fi-shared/app-text';
import { FiOnboardingBuilderStoreService } from '../../../../fi-onboarding-builder-store.service';
import { FiAccessRightHelperService } from '@fi-app/shared/fi-shared-services/fi-access-right-helper.service';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { isDefined } from '@fi-shared/utils';

@Component({
    selector: 'fi-onboarding-access-right-settings',
    templateUrl: './fi-onboarding-access-right-settings.component.html',
    styleUrls: ['./fi-onboarding-access-right-settings.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-onboarding-access-right-settings' },
    standalone: false,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: FiOnboardingAccessRightSettingsComponent,
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: FiOnboardingAccessRightSettingsComponent,
            multi: true,
        },
    ],
})
export class FiOnboardingAccessRightSettingsComponent extends ControlValueAccessorValidatorBase<IAccessRightCoreFilter> {
    public readonly isShared = input.required<boolean>();
    public readonly scrollableElementRefs = input<ElementRef<HTMLElement>[]>([]);
    public readonly hidden = input<boolean>(false);
    private _hiddenEffect = effect(() => {
        this.updateErrorTooltipVisibility();
    });

    @ViewChild('fiSharingSettingsContentComponent')
    fiSharingSettingsContentComponent: FiSharingSettingsContentComponent;

    private readonly fiOnboardingBuilderStoreService = inject(FiOnboardingBuilderStoreService);
    private readonly appTextService = inject(AppTextService);
    private readonly fiAccessRightHelperService = inject(FiAccessRightHelperService);
    private readonly destroyRef = inject(DestroyRef);

    protected accessRightFilterCollection = computed<IAccessRightFilterCollection>(() => {
        return this.fiOnboardingBuilderStoreService.onboardingAccessRightFilterCollection();
    });
    protected selectedFiltersCollection = computed<IAccessRightSelectionFiltersCollection>(() => {
        return this.fiOnboardingBuilderStoreService.selectedFiltersCollection();
    });
    protected savedQuickSelectionFilters = computed<IAccessRightSelectionFiltersCollection[]>(() => {
        return this.fiOnboardingBuilderStoreService.onboardingQuickSelectionFilters();
    });

    protected showErrorTooltip = signal(false);
    private showErrorTooltip$ = toObservable(this.showErrorTooltip);
    protected sharingSettingsConfig = computed<ISharingSettingsContentConfig>(() => {
        if (!isDefined(this.accessRightFilterCollection())) {
            return undefined;
        }
        return {
            availableAccessRightFilterCollection: this.accessRightFilterCollection(),
            moduleType: ModuleType.Onboarding,
            businessUnitIdsOutOfScope: [],
            isItemCreator: true,
            isRolesRequired: this.isShared(),
            showRolesSelect: true,
            showRolesCheckboxes: false,
            accessRightUserFilter: this.accessRightFilterCollection().AccessRightUserFilter,
            areasSectionHeaderText: this.appTextService.get('access_right.select_areas'),
            areasSectionDescriptionText: this.appTextService.get('access_right.select_areas_onboarding_description'),
            rolesSectionHeaderText: this.appTextService.get('onboarding.access_right_roles_heading'),
            rolesSectionDescriptionText: this.appTextService.get('onboarding.access_right_roles_description'),
            individualUnitSectionHeaderText: this.appTextService.get('sharing.individual_units'),
            individualUnitSectionDescriptionText: this.appTextService.get(
                'onboarding.access_right_locations_description',
            ),
            showErrorTooltip$: this.showErrorTooltip$,
            scrollableElementRefs: this.scrollableElementRefs(),
            quickSelectLabelId: this.appTextService.get('access_rights.quick_selection'),
        };
    });

    private _isFormSubmitted = false;
    private _isControlStatusSubscribed = false;
    private _formSubmitEffect = effect(() => {
        const formSubmittedCount = this.fiOnboardingBuilderStoreService.formSubmitted();
        if (!formSubmittedCount || this._isFormSubmitted) {
            return;
        }
        this._isFormSubmitted = true;
        this.trySubscribeOnControlStatusChange();
        this.updateErrorTooltipVisibility();
    });

    private _filtersSaveEffect = effect(() => {
        const filtersSaveCount = this.fiOnboardingBuilderStoreService.onboardingQuickSelectionFiltersSave();
        if (!filtersSaveCount) {
            return;
        }
        this.fiSharingSettingsContentComponent.saveNewQuickSelection();
    });

    private trySubscribeOnControlStatusChange(): void {
        if (!this._isFormSubmitted || !this.controlComponent || this._isControlStatusSubscribed) {
            return;
        }

        this._isControlStatusSubscribed = true;
        this.controlComponent.statusChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.updateErrorTooltipVisibility();
        });
    }

    protected onSavedQuickSelectionFiltersChange(filters: IAccessRightSelectionFiltersCollection[]): void {
        this.fiOnboardingBuilderStoreService.setOnboardingQuickSelectionFilters(filters);
    }
    protected onSelectedFiltersCollectionChange(collection: IAccessRightSelectionFiltersCollection): void {
        this.fiOnboardingBuilderStoreService.setSelectedFiltersCollection(collection);
    }

    protected onAccessRightCoreFilterChange(filter: IAccessRightCoreFilter): void {
        this.ngModel = filter;
        this.doPropagateChanges();
    }

    public doValidate(c: AbstractControl): ValidationErrors {
        this.trySubscribeOnControlStatusChange();
        return {
            ...this.fiAccessRightHelperService.validateAccessRightsCoreFilters(this.ngModel, {
                isShared: this.isShared(),
            }),
            'missing-new-quick-selection-name':
                !!this.fiSharingSettingsContentComponent.isSaveAsNewQuickSelection() &&
                !this.fiSharingSettingsContentComponent.newSelectionFilterModelName?.length,
        };
    }

    private updateErrorTooltipVisibility(): void {
        if (this.hidden()) {
            this.showErrorTooltip.set(false);
            return;
        }
        this.setShowErrorTooltip();
    }

    private setShowErrorTooltip(): void {
        if (!this._isFormSubmitted) {
            this.showErrorTooltip.set(false);
            return;
        }
        const errors = this.controlComponent?.errors ?? {};
        delete errors['role_required'];
        const hasAreaSelectionErrors =
            (!!errors['areas-not-selected'] && !!errors['business-areas-not-selected']) ||
            (!!errors['area_or_unit_or_cluster_required'] && Object.keys(errors).length === 1);
        this.showErrorTooltip.set(hasAreaSelectionErrors);
    }
}
